<script setup lang="ts">
import { useI18n } from 'vue-i18n'

const props = withDefaults(defineProps<Props>(), {
	type: 'hentai',
})

const { t } = useI18n()

interface Props {
	type: 'realistic' | 'hentai' | 'main'
}

function getPlanPreview(index: number): string {
	if (props.type === 'main') {
		return `/image/promo/${index === 1 ? 'realistic' : 'hentai'}_${index}.webp`
	}
	return `/image/promo/${props.type}_${index}.webp`
}
</script>

<template>
	<div class="sm:w-10/12 mx-auto flex flex-col items-center">
		<div class="mx-auto text-center">
			<!-- Lab header with beakers -->
			<div class="relative">
				<div class="bg-slate-800/50 border-[1px] border-solid border-slate-800  rounded-2xl p-8 backdrop-blur-sm">
					<div class="text-slate-400 text-sm uppercase tracking-widest mb-3">
						{{ t('promo.header.welcome') }}
					</div>

					<h1 class="text-transparent bg-gradient-to-r pb-2 from-purple-400 to-pink-600 bg-clip-text lg:text-5xl text-4xl font-extrabold tracking-tight mb-6">
						<template v-if="type === 'hentai'">
							{{ t('promo_hentai.header.unlimited') }}
						</template>
						<template v-if="type === 'realistic'">
							{{ t('promo_realistic.header.unlimited') }}
						</template>
						<template v-if="type === 'main'">
							{{ t('promo.header.unlimited') }}
						</template>
					</h1>

					<div class="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-4 py-2">
						<span class="text-amber-400 text-xs">⚠️</span>
						<span class="text-amber-300 text-sm italic">
							{{ t('promo.header.joke') }}
						</span>
					</div>
					<div class="text-slate-500 text-xsp mt-2">
						{{ t('promo.header.joke_star') }}
					</div>
				</div>
			</div>
		</div>
		<div class="my-[10px] sm:my-[50px] w-32 h-1 bg-gradient-to-r from-purple-500 to-pink-500 mx-auto rounded-full" />
		<div class="flex flex-col items-center gap-6 lg:w-7/12">
			<div class="cursor-pointer p-6 duration-300 hover:-translate-y-2 w-full bg-blue-900/30 border-solid border-blue-600/30 hover:border-blue-600/50 rounded-xl backdrop-blur-sm overflow-hidden group">
				<div class="flex items-center sm:flex-row flex-col justify-between gap-6 sm:gap-4">
					<div class="flex-shrink-0">
						<img
							:src="getPlanPreview(1)"
							class="w-20 h-24 sm:w-24 sm:h-32 object-cover shadow-xl shadow-blue-900/50 rounded-xl group-hover:shadow-2xl group-hover:shadow-blue-800/60 transition-all duration-300 group-hover:scale-105"
							alt="Basic Plan Preview"
							loading="lazy"
						>
					</div>

					<div class="flex-1 text-center sm:text-left space-y-2">
						<div class="pb-4 font-semibold text-blue-100 text-xl sm:text-2xl tracking-tight">
							{{ t('common.basic_plan') }}
						</div>
						<p class="text-blue-300 text-sm font-medium">
							{{ t('shop.subscription.images_per_day', { count: 175 }) }}
						</p>
					</div>

					<div class="flex-shrink-0 text-center space-y-3">
						<div class="space-y-1">
							<div class="text-3xl font-bold text-white">
								$11.99
							</div>
							<div class="text-blue-300 text-xs">
								/ {{ t('durations.month') }}
							</div>
						</div>
						<router-link
							class="button-indigo no-underline !font-semibold tracking-tight"
							to="/tickets"
						>
							{{ t('promo.get_started') }}
						</router-link>
					</div>
				</div>
			</div>
			<div class="cursor-pointer p-6 duration-300 hover:-translate-y-2 w-full bg-gradient-to-br from-purple-900/40 to-pink-900/40 border-solid border-purple-500/30 hover:border-purple-600/50 rounded-xl backdrop-blur-sm overflow-hidden group relative">
				<div class="flex items-center sm:flex-row flex-col justify-between gap-6 sm:gap-4">
					<!-- Image avec effet premium -->
					<div class="flex-shrink-0 relative">
						<div class="absolute inset-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl blur-lg" />
						<img
							:src="getPlanPreview(2)"
							class="relative w-20 h-24 sm:w-24 sm:h-32 object-cover shadow-xl shadow-purple-900/50 rounded-xl group-hover:shadow-2xl group-hover:shadow-purple-800/60 transition-all duration-300 group-hover:scale-105"
							alt="Pro Plan Preview"
							loading="lazy"
						>
					</div>

					<!-- Content avec style premium -->
					<div class="flex-1 text-center sm:text-left space-y-2">
						<div class="pb-4 break-all font-bold text-2xl sm:text-3xl bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent tracking-tight">
							{{ t('common.pro_plan') }} <span class="text-white">✨</span>
						</div>

						<p class="text-purple-300 text-sm font-bold">
							{{ t('promo.header.pro_perk') }}
						</p>
					</div>

					<!-- Premium CTA -->
					<div class="flex-shrink-0 text-center space-y-3">
						<div class="space-y-1">
							<div class="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
								$24.99
							</div>
							<div class="text-purple-300 text-xs">
								/ {{ t('durations.month') }}
							</div>
						</div>
						<router-link
							class="button-rainbow !font-semibold tracking-tight no-underline"
							to="/tickets"
						>
							{{ t('promo.get_started') }}
						</router-link>
					</div>
				</div>
			</div>

			<router-link
				class="button-slate p-button-outlined no-underline"
				to="/create"
			>
				{{ t('promo.header.try_for_free') }}
			</router-link>
		</div>
	</div>
</template>

<style scoped lang="scss">

</style>
